import React, { useMemo, useState } from 'react'
import RfidIdentifierInput from './RfidIdentifierInput'

type Props = {
  tags: IRfidTag[]
}

const RfidInventory = ({ tags }: Props) => {
  const [detectedIdentifiers, setDetectedIdentifiers] = useState<Set<string>>(new Set())
  const [unregisteredIdentifiers, setUnregisteredIdentifiers] = useState<Set<string>>(new Set())

  const tagsByIdentifier = useMemo(
    () => new Map(tags.map((tag) => [tag.identifier, tag])),
    [tags],
  )
  const detectedTags = useMemo(
    () => tags.filter((tag) => detectedIdentifiers.has(tag.identifier)),
    [detectedIdentifiers, tags],
  )
  const missingTags = useMemo(
    () => tags.filter((tag) => !detectedIdentifiers.has(tag.identifier)),
    [detectedIdentifiers, tags],
  )

  const handleRead = (identifier: string) => {
    if (tagsByIdentifier.has(identifier)) {
      setDetectedIdentifiers((current) => new Set(current).add(identifier))
      return
    }
    setUnregisteredIdentifiers((current) => new Set(current).add(identifier))
  }

  const handleReset = () => {
    setDetectedIdentifiers(new Set())
    setUnregisteredIdentifiers(new Set())
  }

  return (
    <section className="rfid-panel" data-testid="rfid-inventory">
      <div className="rfid-panel__header">
        <div>
          <h3>RFID棚卸し</h3>
          <p className="rfid-panel__description">
            選択中のグループに登録された全RFIDを期待集合として、その場で有無を確認します。
            結果は保存されません。
          </p>
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={handleReset}>
          リセット
        </button>
      </div>

      <RfidIdentifierInput id="rfid-inventory-identifier" onRead={handleRead} />

      <div className="rfid-inventory-summary" data-testid="rfid-inventory-summary">
        <div>
          <strong>{detectedTags.length}</strong>
          <span>確認済み</span>
        </div>
        <div>
          <strong>{missingTags.length}</strong>
          <span>未検出</span>
        </div>
        <div>
          <strong>{unregisteredIdentifiers.size}</strong>
          <span>未登録</span>
        </div>
      </div>

      {tags.length === 0 ? (
        <div className="rfid-message">このグループにはRFIDタグが登録されていません。</div>
      ) : (
        <div className="rfid-inventory-columns">
          <div>
            <h4>確認済み</h4>
            {detectedTags.length === 0 ? (
              <p className="rfid-empty-text">まだ読み取られていません。</p>
            ) : (
              <ul>
                {detectedTags.map((tag) => (
                  <li key={tag.id}>
                    <span>{tag.name}</span>
                    <span className="rfid-code">{tag.identifier}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h4>未検出</h4>
            {missingTags.length === 0 ? (
              <p className="rfid-complete-message">すべてのRFIDを確認しました。</p>
            ) : (
              <ul>
                {missingTags.map((tag) => (
                  <li key={tag.id}>
                    <span>{tag.name}</span>
                    <span className="rfid-code">{tag.identifier}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {unregisteredIdentifiers.size > 0 && (
        <div className="rfid-unregistered">
          <h4>未登録の読み取り</h4>
          <ul>
            {Array.from(unregisteredIdentifiers).map((identifier) => (
              <li key={identifier} className="rfid-code">{identifier}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

export default RfidInventory
